const TRANSLATION_CACHE_PREFIX = "i18n:translations:v1:";

type TranslationBundle = Record<string, unknown>;

const isTranslationBundle = (value: unknown): value is TranslationBundle =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getCacheKey = (language: string, namespace: string): string =>
  `${TRANSLATION_CACHE_PREFIX}${encodeURIComponent(language)}:${encodeURIComponent(namespace)}`;

export const getCachedTranslationBundle = (
  language: string,
  namespace: string,
): TranslationBundle | undefined => {
  try {
    const cachedBundle = localStorage.getItem(getCacheKey(language, namespace));
    if (!cachedBundle) {
      return undefined;
    }

    const parsedBundle: unknown = JSON.parse(cachedBundle);
    return isTranslationBundle(parsedBundle) ? parsedBundle : undefined;
  } catch {
    return undefined;
  }
};

export const cacheTranslationBundle = (
  language: string,
  namespace: string,
  bundle: TranslationBundle,
): void => {
  if (!isTranslationBundle(bundle)) {
    return;
  }

  try {
    localStorage.setItem(
      getCacheKey(language, namespace),
      JSON.stringify(bundle),
    );
  } catch {
    // Storage can be unavailable or full; translations remain available in memory.
  }
};

export const clearTranslationCache = (): void => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(TRANSLATION_CACHE_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore storage cleanup failures during logout.
  }
};
